/**
 * Session automatic-logoff API (WS8 / HIPAA §164.312(a)(2)(iii)).
 *
 * Two endpoints, both database-free on the backend:
 *
 *   GET  /api/session/timeout    read the deadlines. Does NOT extend them —
 *                                it is a GET precisely so polling it can't
 *                                keep an abandoned workstation signed in.
 *   POST /api/session/heartbeat  extend the *idle* window. It can never
 *                                extend the absolute cap.
 *
 * Every timestamp is absolute epoch **seconds** from the server. The hook
 * converts to local-clock milliseconds using the skew implied by
 * ``server_time`` rather than trusting the two clocks to agree.
 */
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'

/** Header the SPA sets on requests provoked by genuine user input.
 *  `src/auth/session_timeout.py` refreshes the idle stamp only for requests
 *  carrying this header or using a mutating method — background polling
 *  (the app has ~7 refetchInterval timers) deliberately does not count. */
export const ACTIVITY_HEADER = 'X-Voysi-Activity'
export const ACTIVITY_HEADER_VALUE = 'user'

export interface SessionTimeout {
  /** False only when the backend has no SESSION_SECRET (local dev with no
   *  session store). The UI must not render a countdown to nothing. */
  enforced: boolean
  idle_timeout_seconds: number
  absolute_max_age_seconds: number
  warning_seconds: number
  last_activity_at: number
  auth_started_at: number
  /** Extendable via the heartbeat. Null when the idle check is disabled. */
  idle_expires_at: number | null
  /** NOT extendable — the whole point of the absolute cap. */
  absolute_expires_at: number | null
  server_time: number
}

export function fetchSessionTimeout(): Promise<SessionTimeout> {
  return apiFetch<SessionTimeout>('/api/session/timeout')
}

export function sendSessionHeartbeat(): Promise<SessionTimeout> {
  return apiFetch<SessionTimeout>('/api/session/heartbeat', {
    method: 'POST',
    headers: { [ACTIVITY_HEADER]: ACTIVITY_HEADER_VALUE },
  })
}

/** Fetched once per mount. The windows are configuration, not live state —
 *  the hook derives every deadline locally from the returned epochs, so
 *  there is nothing to poll for. */
export function useSessionTimeoutConfig() {
  return useQuery({
    queryKey: ['session', 'timeout'],
    queryFn: fetchSessionTimeout,
    staleTime: Infinity,
    refetchOnMount: false,
    retry: false,
  })
}
